/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { RadioButtonGroup, RadioButton } from '@carbon/react';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import { noopFn } from '../../../../Utilities/propHelpers/defaultProps.js';
import TableSettingsOption from './TableSettingsOption.js';

var getSizeText = function getSizeText(size, t) {
  switch (size) {
    case 'tall':
      return t('Tall (64px)');
    case 'normal':
      return t('Normal (48px)');
    case 'short':
      return t('Short (32px)');
    case 'compact':
      return t('Compact (24px)');
    case 'xs':
      return t('Extra small (24px)');
    case 'sm':
      return t('Small (32px)');
    case 'md':
      return t('Medium (40px)');
    case 'lg':
      return t('Large (48px)');
    case 'xl':
      return t('Extra large (64px)');
    default:
      return '---';
  }
};
var TableSettingsSize = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var size = _ref.size,
    sizeOptions = _ref.sizeOptions,
    handleMenuItemFocus = _ref.handleMenuItemFocus,
    _ref$onChange = _ref.onChange,
    onChangeProp = _ref$onChange === void 0 ? noopFn : _ref$onChange;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(size),
    _useState2 = _slicedToArray(_useState, 2),
    selected = _useState2[0],
    setSelected = _useState2[1];
  var onChange = function onChange(id) {
    setSelected(id);
    onChangeProp(id);
  };
  return /*#__PURE__*/React.createElement(TableSettingsOption, {
    ref: ref
  }, /*#__PURE__*/React.createElement(RadioButtonGroup, {
    labelPosition: "right",
    name: "row-height-radio-button-group",
    legendText: t('Row height'),
    onChange: onChange,
    orientation: "vertical",
    valueSelected: selected
  }, sizeOptions.map(function (option) {
    return /*#__PURE__*/React.createElement(RadioButton, {
      key: option,
      id: option,
      labelText: getSizeText(option, t),
      value: option,
      onKeyDown: handleMenuItemFocus,
      "data-table-settings-item-focusable": true
    });
  })));
});
TableSettingsSize.propTypes = {
  /**
   * Provide initially selected size
   */
  size: PropTypes.string.isRequired,
  /**
   * Provide an array of size options
   */
  sizeOptions: PropTypes.arrayOf(PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl'])).isRequired,
  /**
   * Provide an optional hook that is called each time a key is pressed
   */
  handleMenuItemFocus: PropTypes.func,
  /**
   * Provide an optional hook that is called each time the selection is updated
   */
  onChange: PropTypes.func
};
var TableSettingsSize$1 = TableSettingsSize;

export { TableSettingsSize$1 as default, getSizeText };
